<template>
  <div class="mx-auto p-4 w-full max-w-7xl">
    <div class="flex mt-4 text-base">
      <article class="flex-1">
        <h1 class="heading-1 mb-2">{{ course.code }}</h1>
        <h2 class="heading-5 mb-2">{{ courseDisplayHeading }}</h2>
        <div class="text-small mb-10">
          <template v-if="!hideDepartments">
            <NuxtLink
              v-for="department of courseDepartments"
              :key="department.id"
              :to="`/departments/${encodeURIComponent(department.id)}/overview`"
            >
              {{ department.displayName || department.name }}
            </NuxtLink>
            <BaseIcon
              v-if="course.career"
              icon="circle"
              class="mx-4 icon-sm"
            />
          </template>
          <span>{{ course.career }}</span>
          <BaseIcon
            v-if="course.college"
            icon="circle"
            class="mx-4 icon-sm"
          />
          <span>{{ course.college }}</span>
        </div>
        <HorizontalScroll class="border-b">
          <BaseTabs
            :tabs="tabs"
            :value="activeTab"
            @input="activeTabKey = $event.key"
          >
            <template #default="{ tab, isActive }">
              <span
                class="tab-label text-black font-bold pb-2 transition-all duration-300 ease-in-out border-transparent"
                :class="{ 'tab-active heading-5 border-black': isActive }"
              >
                {{ tab.label }}
              </span>
            </template>
          </BaseTabs>
        </HorizontalScroll>
        <section>
          <transition name="fade-in">
            <DynamicFieldsRenderer
              v-if="fieldRows.length"
              :key="activeTemplateCard.id"
              class="mt-8 mb-2 -mx-2"
              :fields="fieldRows"
              :questions="Object.values(coursePageTemplate.questions)"
              :document-value="course"
              :value="course"
            />
            <EmptyState
              v-else
              :key="activeTemplateCard.id"
              class="bg-gray-50 rounded p-16 my-4"
            />
          </transition>
        </section>
      </article>
      <slot name="sidebar" />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import DynamicFieldsRenderer from '@/components/contemporaryTheme/components/DynamicFieldsRenderer'
import EmptyState from '@/components/contemporaryTheme/components/Base/EmptyState'
import HorizontalScroll from '@/components/HorizontalScroll'
import { courseNameWithFallbacks } from '@/utils/entityNames'
import useFeatureFlags from '@/composables/useFeatureFlags'
import {
  HIDE_DEPARTMENTS,
  TITLE_INTERPOLATIONS,
} from '@/constants/featureFlags'
import useInterpolation from '@/composables/useInterpolation'
import { generateCourseLabel } from '@/utils/dataLabels'

export default {
  setup() {
    const { hideDepartments, enableTitleInterpolations } = useFeatureFlags({
      hideDepartments: HIDE_DEPARTMENTS,
      enableTitleInterpolations: TITLE_INTERPOLATIONS,
    })
    const { getInterpolatedFieldValue } = useInterpolation()
    return {
      hideDepartments,
      courseNameWithFallbacks,
      enableTitleInterpolations,
      getInterpolatedFieldValue,
    }
  },
  components: {
    DynamicFieldsRenderer,
    EmptyState,
    HorizontalScroll,
  },
  props: {
    course: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      activeTabKey: null,
    }
  },
  computed: {
    ...mapState('settings', ['coursePageTemplate']),
    ...mapState('departments', { departments: (state) => state.all }),
    activeTab() {
      return this.tabs.find((t) => t.key === this.activeTabKey) || this.tabs[0]
    },
    tabs() {
      return this.coursePageTemplate.template.map((card) => ({
        key: card.id,
        label: card.config.title,
      }))
    },
    activeTemplateCard() {
      return this.coursePageTemplate.template.find(
        (card) => card.id === this.activeTab.key
      )
    },
    fieldRows() {
      return this.activeTemplateCard.children.reduce((acc, row) => {
        acc.push([...row.children])
        return acc
      }, [])
    },
    courseDepartments() {
      return (this.course.departments || []).map(
        (dep) =>
          typeof dep === 'object'
            ? dep
            : this.departments.find((el) => el.id === dep) || {
                id: dep,
                displayName: dep,
              }
      )
    },
    courseDisplayHeading() {
      return generateCourseLabel(this.course, undefined, {
        getInterpolatedFieldValue: this.enableTitleInterpolations
          ? this.getInterpolatedFieldValue
          : undefined,
        nameOnlyFallback: true,
      })
    },
  },
}
</script>

<style scoped lang="scss">
@import '@/assets/css/contemporaryTheme/tabbedTemplate.scss';
</style>
